import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../store/services/api";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { decodeUserJWT } from "../../extras";
import {
  canManage,
  validatePhone,
  validatePhoneDDD,
  validateString,
} from "../../common/validators";

import { getAddressLatLng } from "../../utils/address";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { extractApiErrorMessage } from "../../utils/subscriptionErrors";
import AddressService from "../../api/address";
import DeliveryCenterService from "../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";

// Components
import AddressModal from "./AddressModal";
import ContactModal from "./ContactModal";
import OperatorsModal from "./OperatorsModal";
import FiscalDataModal from "./FiscalDataModal";
import AssistantWelcome from "../xRay/AssistantWelcome";
import DeliveryTeamModal from "./DeliveryTeamModal";
import DevicesListModal from "./Devices/DevicesListModal";
import PaymentMethodsModal from "./PaymentMethodsModal";
import DiscountCouponsModal from "./DiscountCouponsModal";
import DeliveryRadiusListModal from "./DeliveryRadiusListModal";
import ShippingRoutesConfigModal from "./ShippingRoutesConfigModal";
import SmartRoutingConfigModal from "./SmartRoutingConfigModal";
import DeliveryIntegrationsListModal from "./DeliveryIntegrationsListModal";
import WorkShiftListModal from "./workShift/WorkShiftListModal";
import NeighborhoodGroupListModal from "./neighborhoodGroups/NeighborhoodGroupListModal";
import EquipmentListModal from "./equipment/EquipmentListModal";
import { DELIVERY_CENTER_WELCOME_MESSAGE } from "../../types/welcomeMessages";

// Material UI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import {
  alpha,
  Backdrop,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Icons
import MopedIcon from "@mui/icons-material/Moped";
import PrintIcon from "@mui/icons-material/Print";
import GroupsIcon from "@mui/icons-material/Groups";
import CancelIcon from "@mui/icons-material/Cancel";
import DiscountIcon from "@mui/icons-material/Discount";
import PaymentsIcon from "@mui/icons-material/Payments";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AirlineStopsIcon from "@mui/icons-material/AirlineStops";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import FilterTiltShiftIcon from "@mui/icons-material/FilterTiltShift";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeliveryServicesModal from "./DeliveryService/DeliveryServicesModal";
import DashboardScreenConfigsModal from "./Dashboard/DashboardScreenConfigsModal";
import SavingsIcon from "@mui/icons-material/Savings";
import ChatIcon from "@mui/icons-material/Chat";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CashierTypeConfigsModal from "./Cashier/CashierTypeConfigsModal";
import PageHeader from "../page/PageHeader";
import IfoodChatManagerModal from "./iFood/IfoodChatManagerModal";
import AutoPilotConfigModal from "./AutoPilot/AutoPilotConfigModal";
import { AirplaneTicket, WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import OrderTicketConfigsModal from "./OrderTicket/OrderTicketConfigsModal";
import OrderFlowConfigsModal from "./OrderFlowConfigsModal";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DeliveryConfirmationConfigsModal from "./Logistics/DeliveryConfirmationConfigsModal";
import AgentsModal from "./Agents/AgentsModal";
import NotificationsConfigModal from "./Notifications/NotificationsConfigModal";
import WhatsAppInstanceModal from "./WhatsApp/WhatsAppInstanceModal";
import FiscalBotNumbersModal from "./WhatsApp/FiscalBotNumbersModal";
import Food99StoresModal from "./Food99/Food99StoresModal";
import StorefrontIcon from "@mui/icons-material/Storefront";

// --- Internal Components ---

const SettingsRow = ({
  icon: Icon,
  title,
  description,
  status,
  onClick,
  recommended,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Paper
      elevation={0}
      component={motion.div}
      whileHover={{
        scale: 1.01,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 3,
        cursor: "pointer",
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.1),
        display: "flex",
        alignItems: "center",
        transition: "all 0.2s ease",
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 20px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
        },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: "50%",
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          mr: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon fontSize={isMobile ? "small" : "medium"} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
          {recommended && (
            <Chip
              label="Recomendado"
              color="secondary"
              size="small"
              sx={{ ml: 1, height: 20, fontSize: "0.65rem", fontWeight: 700 }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap>
          {description}
        </Typography>
      </Box>

      {!isMobile && status !== undefined && (
        <Chip
          icon={status ? <CheckCircleIcon /> : <CancelIcon />}
          label={status ? "Configurado" : "Pendente"}
          color={status ? "success" : "default"}
          variant={status ? "filled" : "outlined"}
          size="small"
          sx={{ mr: 2, fontWeight: 600 }}
        />
      )}

      <IconButton size="small" sx={{ color: "text.secondary" }}>
        <ChevronRightIcon />
      </IconButton>
    </Paper>
  );
};

const PendingConfigBanner = ({ missingItems, onRegister, canRegister }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        mb: 4,
        borderRadius: "16px",
        position: "relative",
        overflow: "hidden",
        background: `
          linear-gradient(
            135deg,
            ${theme.palette.warning.dark} 0%,
            ${theme.palette.warning.main} 30%,
            ${theme.palette.error.main} 70%,
            ${alpha(theme.palette.error.dark, 0.9)} 100%
          )
        `,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 15% 85%, ${alpha(
              theme.palette.warning.light,
              0.35
            )} 0%, transparent 70%),
            radial-gradient(ellipse 60% 50% at 85% 15%, ${alpha(
              theme.palette.error.light,
              0.25
            )} 0%, transparent 70%)
          `,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          backdropFilter: "blur(40px) saturate(1.2)",
          background: alpha("#fff", 0.05),
          borderRadius: "inherit",
        },
      }}
    >
      {/* Floating orbs */}
      <Box
        sx={{
          position: "absolute",
          top: -30,
          right: -15,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            "#fff",
            0.1
          )} 0%, transparent 70%)`,
          filter: "blur(2px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -25,
          left: 40,
          width: 90,
          height: 90,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.warning.light,
            0.15
          )} 0%, transparent 70%)`,
          filter: "blur(4px)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Glass icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              bgcolor: alpha("#fff", 0.12),
              border: `1px solid ${alpha("#fff", 0.2)}`,
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 8px 32px ${alpha("#000", 0.12)}`,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#fff",
                lineHeight: 1.3,
                mb: 0.5,
                textShadow: `0 1px 8px ${alpha("#000", 0.15)}`,
              }}
            >
              Sua central ainda não está ativa
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha("#fff", 0.85),
                display: "block",
                lineHeight: 1.4,
                mb: 1.5,
                textShadow: `0 1px 4px ${alpha("#000", 0.1)}`,
              }}
            >
              Complete as configurações para começar a receber pedidos
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {missingItems.map((item, index) => (
                <Chip
                  key={index}
                  label={item.label}
                  size="small"
                  onClick={item.action}
                  icon={
                    <CancelIcon
                      sx={{ color: `${alpha("#fff", 0.9)} !important` }}
                    />
                  }
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    bgcolor: alpha("#fff", 0.15),
                    color: "#fff",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    backdropFilter: "blur(8px)",
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.25),
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        {canRegister && (
          <Button
            variant="contained"
            size="large"
            onClick={onRegister}
            sx={{
              whiteSpace: "nowrap",
              minWidth: 180,
              py: 1.25,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.9rem",
              bgcolor: alpha("#fff", 0.2),
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.3)}`,
              backdropFilter: "blur(12px)",
              boxShadow: `0 4px 20px ${alpha("#000", 0.15)}`,
              "&:hover": {
                bgcolor: alpha("#fff", 0.3),
                boxShadow: `0 6px 28px ${alpha("#000", 0.2)}`,
              },
            }}
          >
            Ativar Central
          </Button>
        )}
      </Box>
    </Box>
  );
};

// --- Main Component ---

export default function DeliveryCenterCreateEdit(props) {
  const theme = useTheme();
  const auth = useSelector((state) => state.auth);
  const userType = useSelector((state) => state.auth.user?.user_type);
  const userId = useSelector((state) => state.auth?.user?.username);

  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmLabel, setConfirmLabel] = useState("");
  const [confirmWhat, setConfirmWhat] = useState(0);

  const [deliveryCenterId, setDeliveryCenterId] = useState(null);
  const [deliveryCenterFiscalId, setDeliveryCenterFiscalId] = useState(null);

  const [showContactModal, setShowContactModal] = useState(false);
  const [phoneDDD, setphoneDDD] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pontalogUsername, setPontalogUsername] = useState(null);
  const [pontalogPassword, setPontalogPassword] = useState(null);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressId, setAddressId] = useState(uuidv4());
  const [cep, setCep] = useState("");
  const [name, setName] = useState("Central");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [complement, setComplement] = useState("");
  const [reference, setReference] = useState("");
  const [city, setCity] = useState("");
  const [uf, setUf] = useState("");

  const [showFiscalDataModal, setShowFiscalDataModal] = useState(false);
  const [showDashboardScreenConfigsModal, setShowDashboardScreenConfigsModal] =
    useState(false);
  const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
  const [showCashierTypesModal, setShowCashierTypesModal] = useState(false);
  const [showOrderTicketModal, setShowOrderTicketModal] = useState(false);
  const [showOrderFlowConfigsModal, setShowOrderFlowConfigsModal] = useState(false);
  const [showIfoodChatConfigModal, setShowIfoodChatConfigModal] =
    useState(false);
  const [showAutoPilotConfigModal, setShowAutoPilotConfigModal] =
    useState(false);
  const [showDiscountCouponsModal, setShowDiscountCouponsModal] =
    useState(false);
  const [showDeliveryRadiusModal, setShowDeliveryRadiusModal] = useState(false);
  const [showDeliveryGuysModal, setShowDeliveryGuysModal] = useState(false);
  const [showDeliveryServicesModal, setShowDeliveryServicesModal] =
    useState(false);
  const [showiFoodDeliveryServicesModal, setShowiFoodDeliveryServicesModal] =
    useState(false);
  const [showDeliveryIntegrationsModal, setShowDeliveryIntegrationsModal] =
    useState(false);
  const [showOperatorsModal, setShowOperatorsModal] = useState(false);
  const [showPrintersModal, setShowPrintersModal] = useState(false);
  const [showWorkShiftsModal, setShowWorkShiftsModal] = useState(false);
  const [showEquipmentsModal, setShowEquipmentsModal] = useState(false);
  const [showNeighborhoodGroupModal, setShowNeighborhoodGroupModal] =
    useState(false);
  const [showShippingRoutesConfigModal, setShowShippingRoutesConfigModal] =
    useState(false);
  const [showSmartRoutingConfigModal, setShowSmartRoutingConfigModal] =
    useState(false);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showFood99Modal, setShowFood99Modal] = useState(false);
  const [showFiscalBotNumbersModal, setShowFiscalBotNumbersModal] =
    useState(false);

  const [saloonFee, setSaloonFee] = useState(null);
  const [counterFee, setCounterFee] = useState(null);

  const [showWelcome, setShowWelcome] = useState(false);

  // Calculate completed items for progress tracking
  const allCompletedItems = [
    phoneNumber.length >= 8,
    !!cep,
    !!deliveryCenterFiscalId,
  ].filter(Boolean).length;

  const totalRequiredItems = 3;

  const configurationCards = useMemo(() => {
    const generalCards = [
      {
        icon: PhoneIcon,
        title: "Contato",
        description: "Configure telefone e informações de contato da central",
        status: phoneNumber.length >= 8,
        onClick: () => setShowContactModal(true),
        recommended: true,
        searchable: true,
      },
      {
        icon: BusinessIcon,
        title: "Endereço",
        description: "Configure o endereço físico da sua central de operações",
        status: !!cep,
        onClick: () => setShowAddressModal(true),
        recommended: true,
        searchable: true,
      },
      {
        icon: GroupsIcon,
        title: "Equipe",
        description: "Gerencie operadores e permissões",
        onClick: () => setShowOperatorsModal(true),
        searchable: true,
      },
      {
        icon: FilterTiltShiftIcon,
        title: "Turnos de Trabalho",
        description: "Configure escalas e turnos da sua equipe",
        onClick: () => setShowWorkShiftsModal(true),
        searchable: true,
      },
      {
        icon: NotificationsIcon,
        title: "Notificações",
        description: "Configure notificações e emails da sua operação",
        onClick: () => setShowNotificationsModal(true),
        searchable: true,
      },
      {
        icon: WhatsAppIcon,
        title: "WhatsApp",
        description: "Gerencie sua instância WhatsApp",
        onClick: () => setShowWhatsAppModal(true),
        searchable: true,
      },
      {
        icon: WhatsAppIcon,
        title: "Bot de Notas WhatsApp",
        description:
          "Autorize os números que podem enviar notas fiscais para leitura automática",
        onClick: () => setShowFiscalBotNumbersModal(true),
        searchable: true,
      },
      {
        icon: StorefrontIcon,
        title: "99Food",
        description: "Conecte suas lojas do 99Food e receba pedidos",
        onClick: () => setShowFood99Modal(true),
        searchable: true,
      },
    ];

    const operationCards = [
      {
        icon: DashboardIcon,
        title: "Tela Central",
        description: "Configurações da Tela Central (KDS)",
        onClick: () => setShowDashboardScreenConfigsModal(true),
        searchable: true,
      },
      {
        icon: PrintIcon,
        title: "Impressoras",
        description: "Configure impressoras térmicas e de comprovantes",
        onClick: () => setShowPrintersModal(true),
        searchable: true,
      },
      {
        icon: SettingsRemoteIcon,
        title: "Maquininhas",
        description: "Configure maquininhas de cartão",
        onClick: () => setShowEquipmentsModal(true),
        searchable: true,
      },
      ...(canManage(userType)
        ? [
            {
              icon: AirplaneTicket,
              title: "Comanda",
              description: "Configure sua comanda ideal",
              onClick: () => setShowOrderTicketModal(true),
              searchable: true,
            },
            {
              icon: SavingsIcon,
              title: "Tipos de Caixa",
              description:
                "Defina os tipos de caixa disponíveis na sua operação",
              onClick: () => setShowCashierTypesModal(true),
              searchable: true,
            },
            {
              icon: LocalShippingIcon,
              title: "Fluxo de Pedidos",
              description:
                "Configure o fluxo de processamento dos pedidos",
              onClick: () => setShowOrderFlowConfigsModal(true),
              searchable: true,
            },
            {
              icon: SmartToyIcon,
              title: "Piloto Automático",
              description: "O Slapie aceitará pedidos automaticamente",
              onClick: () => setShowAutoPilotConfigModal(true),
              searchable: true,
            },
            {
              icon: ChatIcon,
              title: "Chat iFood",
              description: "Gerencie o chat integrado do iFood",
              onClick: () => setShowIfoodChatConfigModal(true),
              searchable: true,
            },
            {
              icon: SmartToyIcon,
              title: "Agentes IA",
              description: "Gerencie agentes IA para automação",
              onClick: () => setShowAgentsModal(true),
              searchable: true,
            },
          ]
        : []),
    ];

    const logisticCards = [
      {
        icon: TrackChangesIcon,
        title: "Raios de Entrega",
        description: "Configure distâncias, taxas e tempos de entrega",
        onClick: () => setShowDeliveryRadiusModal(true),
        searchable: true,
      },
      {
        icon: MultipleStopIcon,
        title: "Matriz de Bairros",
        description: "Configure taxas e tempos por regiões e bairros",
        onClick: () => setShowNeighborhoodGroupModal(true),
        searchable: true,
      },
      {
        icon: MopedIcon,
        title: "Integrações Logísticas",
        description: "Integre com serviços de entrega externos",
        onClick: () => setShowDeliveryIntegrationsModal(true),
        searchable: true,
      },
      {
        icon: AirlineStopsIcon,
        title: "Rotas de Entrega",
        description: "Configure rotas otimizadas para suas entregas",
        onClick: () => setShowShippingRoutesConfigModal(true),
        searchable: true,
      },
      {
        icon: AltRouteIcon,
        title: "Roteirização Inteligente",
        description:
          "Agrupa pedidos embalados automaticamente por direção e distância",
        onClick: () => setShowSmartRoutingConfigModal(true),
        searchable: true,
      },
      {
        icon: SportsMotorsportsIcon,
        title: "Entregadores",
        description: "Gerencie sua equipe de entregadores",
        onClick: () => setShowDeliveryGuysModal(true),
        searchable: true,
      },
      {
        icon: GroupsIcon,
        title: "Disk Motos",
        description: "Gerencie seus disk motos cadastrados",
        onClick: () => setShowDeliveryServicesModal(true),
        searchable: true,
      },
      {
        icon: DeliveryDiningIcon,
        title: "Confirmação de Entrega iFood",
        description: "Confirmações de entrega ifood",
        onClick: () => setShowiFoodDeliveryServicesModal(true),
        searchable: true,
      },
    ];

    const financeCards = [
      {
        icon: PaymentsIcon,
        title: "Métodos de Pagamento",
        description:
          "Configure as formas de pagamento aceitas pelo seu delivery",
        onClick: () => setShowPaymentMethodsModal(true),
        searchable: true,
      },
      {
        icon: ReceiptIcon,
        title: "Módulo Fiscal",
        description: "Configurações fiscais, impostos e emissão de notas",
        status: !!deliveryCenterFiscalId,
        onClick: () => setShowFiscalDataModal(true),
        recommended: true,
        searchable: true,
      },
      {
        icon: DiscountIcon,
        title: "Cupons de Desconto",
        description: "Crie e gerencie cupons promocionais",
        onClick: () => setShowDiscountCouponsModal(true),
        searchable: true,
      },
    ];

    return {
      general: generalCards,
      operation: operationCards,
      logistic: logisticCards,
      finance: financeCards,
      all: [
        ...generalCards,
        ...operationCards,
        ...logisticCards,
        ...financeCards,
      ],
    };
  }, [userType, phoneNumber, cep, deliveryCenterFiscalId]);

  const filteredCards = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase().trim();

    if (!searchTermLower) return configurationCards;

    const filterCards = (cards) =>
      cards.filter(
        (card) =>
          card.searchable &&
          (card.title.toLowerCase().includes(searchTermLower) ||
            card.description.toLowerCase().includes(searchTermLower))
      );

    return {
      general: filterCards(configurationCards.general),
      operation: filterCards(configurationCards.operation),
      logistic: filterCards(configurationCards.logistic),
      finance: filterCards(configurationCards.finance),
      all: filterCards(configurationCards.all),
    };
  }, [searchTerm, configurationCards]);

  const hasResultsInTab = useMemo(() => {
    return {
      0: filteredCards.general.length > 0,
      1: filteredCards.operation.length > 0,
      2: filteredCards.logistic.length > 0,
      3: filteredCards.finance.length > 0,
    };
  }, [filteredCards]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const firstTabWithResults = Object.entries(hasResultsInTab).find(
        ([, hasResults]) => hasResults
      );
      if (firstTabWithResults) {
        setTabValue(parseInt(firstTabWithResults[0]));
      }
    }
  }, [searchTerm, hasResultsInTab]);

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const loadData = async () => {
    try {
      const completedUsers =
        JSON.parse(localStorage.getItem("deliveryCenterWelcomeSlapie")) || [];

      setShowWelcome(!completedUsers.includes(userId));

      const { data } = await api.get("/delivery-center");

      if (data.length > 0) {
        const central = data[0];

        setDeliveryCenterId(central.center_id);
        setDeliveryCenterFiscalId(central.fk_fiscal_info);
        setAddressId(central.fk_address);

        setphoneDDD(central.phone_ddd);
        setPhoneNumber(central.phone_number);
        setPontalogUsername(central.ponta_log_username);
        setPontalogPassword(central.ponta_log_password);

        setCep(central.cep);
        setStreet(central.street);
        setHouseNumber(central.house_number);
        setNeighborhood(central.neighborhood);
        setComplement(central.address_complement);
        setReference(central.address_reference);
        setCity(central.city);
        setUf(central.uf);

        setSaloonFee(central.operator_service_fee_saloon);
        setCounterFee(central.operator_service_fee_counter);
      }
    } catch (e) {
      console.error(e);
      const message =
        e.response?.data || "Erro ao carregar dados do centro de entrega.";
      dispatchSnackBarMessage(
        `delivery-center-load${new Date()}`,
        message,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  const handleConfirmModal = (updateWhat) => {
    switch (updateWhat) {
      case 0: // CONTACT
        setConfirmLabel("Atualizar Contato");
        setConfirmMessage(
          "Deseja atualizar o contato da sua central delivery?"
        );
        setConfirmWhat(0);
        break;
      case 1: // ADDRESS
        setConfirmLabel("Atualizar Endereço");
        setConfirmMessage(
          "Deseja atualizar o endereço da sua central delivery?"
        );
        setConfirmWhat(1);
        break;
      default:
        break;
    }
    setShowConfirmModal(!showConfirmModal);
  };

  const handleConfirmAction = () => {
    switch (confirmWhat) {
      case 0:
        break;
      case 1:
        return editAddress();
      default:
        break;
    }
  };

  const createAddress = async () => {
    try {
      const coordinates = await getAddressLatLng(
        street,
        houseNumber,
        neighborhood,
        city
      );
      const address = {
        address_id: addressId,
        street: street,
        neighborhood: neighborhood,
        house_number: houseNumber,
        address_complement: complement,
        address_reference: reference,
        cep: cep,
        city: city,
        uf: uf,
        lat: coordinates.lat.toFixed(7),
        long: coordinates.lng.toFixed(7),
      };

      await AddressService.createAddress(address);
    } catch (e) {
      const message = e.response?.data || "Erro ao cadastrar endereço";
      dispatchSnackBarMessage(`operators-list-${new Date()}`, message, "error");
    }
  };

  const editAddress = async () => {
    try {
      const coordinates = await getAddressLatLng(
        street,
        houseNumber,
        neighborhood,
        city
      );
      const address = {
        address_id: addressId,
        street: street,
        neighborhood: neighborhood,
        house_number: houseNumber,
        address_complement: complement,
        address_reference: reference,
        cep: cep,
        city: city,
        uf: uf,
        lat: coordinates.lat.toFixed(7),
        long: coordinates.lng.toFixed(7),
      };

      await AddressService.editAddress(addressId, address);

      await loadData();
      setShowConfirmModal(!showConfirmModal);
      closeAddressModal();

      dispatchSnackBarMessage(
        `delivery-center-address-edited-${new Date()}`,
        "Endereço alterado com sucesso",
        "success"
      );
    } catch (e) {
      const message = e.response?.data || "Erro ao editar endereço";
      dispatchSnackBarMessage(`operators-list-${new Date()}`, message, "error");
    }
  };

  const createDeliveryCenter = async () => {
    try {
      const contactValidation =
        validatePhoneDDD(phoneDDD) || validatePhone(phoneNumber);

      if (!contactValidation) {
        return dispatchSnackBarMessage(
          `delivery-center-contact-${new Date()}`,
          "Favor preencher as informações de contato da sua central delivery",
          "error"
        );
      }

      const addressValidation =
        validateString(street) ||
        validateString(neighborhood) ||
        validateString(houseNumber) ||
        validateString(cep) ||
        validateString(city) ||
        validateString(uf);

      if (!addressValidation) {
        return dispatchSnackBarMessage(
          `delivery-center-address-${new Date()}`,
          "Seu endereço está com campos vazios, preencha todos antes de continuar",
          "error"
        );
      }

      await createAddress();

      const user_id = decodeUserJWT(auth.user.access);
      const delivery_center = {
        center_id: uuidv4(),
        center_name: name,
        fk_user: user_id,
        fk_address: addressId,
        ponta_log_username: pontalogUsername,
        ponta_log_password: pontalogPassword,
        phone_ddd: phoneDDD,
        phone_number: phoneNumber,
      };

      await DeliveryCenterService.createDeliveryCenter(delivery_center);

      dispatchSnackBarMessage(
        `delivery-center-created-${new Date()}`,
        "Central de entrega cadastrada com sucesso!",
        "success"
      );

      props.history.push("/central-delivery");
    } catch (e) {
      const message = extractApiErrorMessage(e, "Erro ao cadastrar operação");
      dispatchSnackBarMessage(`operators-list-${new Date()}`, message, "error");
    }
  };

  // Memoized callbacks
  const closeContactModal = useCallback(() => setShowContactModal(false), []);
  const closeAddressModal = useCallback(() => setShowAddressModal(false), []);
  const closeFiscalDataModal = useCallback(
    () => setShowFiscalDataModal(false),
    []
  );
  const closePaymentMethodsModal = useCallback(
    () => setShowPaymentMethodsModal(false),
    []
  );
  const closeDiscountCouponsModal = useCallback(
    () => setShowDiscountCouponsModal(false),
    []
  );
  const closeCashierTypesModal = useCallback(
    () => setShowCashierTypesModal(false),
    []
  );
  const closeOrderTicketModal = useCallback(
    () => setShowOrderTicketModal(false),
    []
  );
  const closeOrderFlowConfigsModal = useCallback(
    () => setShowOrderFlowConfigsModal(false),
    []
  );
  const closeIfoodChatConfigModal = useCallback(
    () => setShowIfoodChatConfigModal(false),
    []
  );
  const closeAutoPilotConfigModal = useCallback(
    () => setShowAutoPilotConfigModal(false),
    []
  );
  const closeDeliveryRadiusModal = useCallback(
    () => setShowDeliveryRadiusModal(false),
    []
  );
  const closeDeliveryIntegrationsModal = useCallback(
    () => setShowDeliveryIntegrationsModal(false),
    []
  );
  const closeOperatosModal = useCallback(
    () => setShowOperatorsModal(false),
    []
  );
  const closePrintersModal = useCallback(() => setShowPrintersModal(false), []);
  const closeDeliveryGuysModal = useCallback(
    () => setShowDeliveryGuysModal(false),
    []
  );
  const closeIfoodDeliveryServicesModal = useCallback(
    () => setShowiFoodDeliveryServicesModal(false),
    []
  );
  const closeDeliveryServicesModal = useCallback(
    () => setShowDeliveryServicesModal(false),
    []
  );
  const closeShippingRoutesConfigModal = useCallback(
    () => setShowShippingRoutesConfigModal(false),
    []
  );
  const closeSmartRoutingConfigModal = useCallback(
    () => setShowSmartRoutingConfigModal(false),
    []
  );
  const closeWorkShiftsModal = useCallback(
    () => setShowWorkShiftsModal(false),
    []
  );
  const closeEquipmentsModal = useCallback(
    () => setShowEquipmentsModal(false),
    []
  );
  const closeNeighborhoodGroupModal = useCallback(
    () => setShowNeighborhoodGroupModal(false),
    []
  );
  const closeDashboardScreenConfig = useCallback(
    () => setShowDashboardScreenConfigsModal(false),
    []
  );
  const closeAgentsModal = useCallback(() => setShowAgentsModal(false), []);
  const closeNotificationsModal = useCallback(
    () => setShowNotificationsModal(false),
    []
  );
  const closeWhatsAppModal = useCallback(() => setShowWhatsAppModal(false), []);
  const closeFood99Modal = useCallback(() => setShowFood99Modal(false), []);
  const closeFiscalBotNumbersModal = useCallback(
    () => setShowFiscalBotNumbersModal(false),
    []
  );

  const handleCloseWelcome = useCallback(() => {
    const completedUsers =
      JSON.parse(localStorage.getItem("deliveryCenterWelcomeSlapie")) || [];

    localStorage.setItem(
      "deliveryCenterWelcomeSlapie",
      JSON.stringify([...completedUsers, userId])
    );
    setShowWelcome(false);
  }, [userId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Missing items for banner
  const missingItems = useMemo(() => {
    const items = [];
    if (phoneNumber.length < 8) {
      items.push({
        label: "Contato",
        action: () => setShowContactModal(true),
      });
    }
    if (!cep) {
      items.push({
        label: "Endereço",
        action: () => setShowAddressModal(true),
      });
    }
    return items;
  }, [phoneNumber, cep]);

  if (loading) {
    return (
      <Backdrop
        open={true}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(4px)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="primary" size={60} thickness={4} />
      </Backdrop>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, md: 6 }, minHeight: "100vh" }}>
      <Container maxWidth="lg">
        <PageHeader
          title="Configurações"
          subtitle="Gerencie todos os aspectos da sua operação"
          completedItems={allCompletedItems}
          totalItems={totalRequiredItems}
        />

        {/* Action Required Banner */}
        <AnimatePresence>
          {(!deliveryCenterId || missingItems.length > 0) && (
            <motion.div
              initial={{ opacity: 0, height: 0, mb: 0 }}
              animate={{ opacity: 1, height: "auto", mb: 32 }}
              exit={{ opacity: 0, height: 0, mb: 0 }}
              transition={{ duration: 0.3 }}
            >
              <PendingConfigBanner
                missingItems={missingItems}
                onRegister={createDeliveryCenter}
                canRegister={missingItems.length === 0}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search */}
        <Box sx={{ mb: 4, position: "relative", zIndex: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar configuração..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 4,
                bgcolor: "background.paper",
                boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "primary.main" },
                "&.Mui-focused": {
                  boxShadow: "0 8px 30px 0 rgba(0,0,0,0.1)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              },
            }}
          />
        </Box>

        {/* Categories Navigation */}
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderRadius: 50,
                mr: 1.5,
                px: 3,
                minHeight: 40,
                color: "text.secondary",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  color: "#fff",
                  bgcolor: "primary.main",
                  borderColor: "primary.main",
                  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.15)",
                },
                "&:hover:not(.Mui-selected)": {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label="Geral" iconPosition="start" />
            <Tab label="Operação" iconPosition="start" />
            <Tab label="Logística" iconPosition="start" />
            <Tab label="Financeiro" iconPosition="start" />
          </Tabs>
        </Box>

        {/* List Content */}
        <Box component={motion.div} layout>
          <AnimatePresence mode="wait">
            <motion.div
              key={tabValue}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {tabValue === 0 &&
                filteredCards.general.map((card, i) => (
                  <SettingsRow key={i} {...card} />
                ))}
              {tabValue === 1 &&
                filteredCards.operation.map((card, i) => (
                  <SettingsRow key={i} {...card} />
                ))}
              {tabValue === 2 &&
                filteredCards.logistic.map((card, i) => (
                  <SettingsRow key={i} {...card} />
                ))}
              {tabValue === 3 &&
                filteredCards.finance.map((card, i) => (
                  <SettingsRow key={i} {...card} />
                ))}

              {/* Empty State */}
              {((tabValue === 0 && filteredCards.general.length === 0) ||
                (tabValue === 1 && filteredCards.operation.length === 0) ||
                (tabValue === 2 && filteredCards.logistic.length === 0) ||
                (tabValue === 3 && filteredCards.finance.length === 0)) && (
                <Box sx={{ textAlign: "center", py: 8, opacity: 0.7 }}>
                  <SearchIcon sx={{ fontSize: 60, color: "text.disabled" }} />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Nenhum resultado encontrado
                  </Typography>
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Container>

      {/* Modals (Keep existing ones) */}
      <AnimatedModal
        isOpen={showWelcome}
        onClose={handleCloseWelcome}
        width="90%"
        maxWidth="480px"
        zIndex={9999}
      >
        <ModalContent padding={0}>
          <AssistantWelcome
            title="Bem-vindo às Configurações"
            message={DELIVERY_CENTER_WELCOME_MESSAGE}
            onClose={handleCloseWelcome}
          />
        </ModalContent>
      </AnimatedModal>

      <AnimatedModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        width="400px"
      >
        <ModalHeader
          title={confirmLabel}
          onClose={() => setShowConfirmModal(false)}
        />
        <ModalContent>
          <Typography>{confirmMessage}</Typography>
        </ModalContent>
        <ModalFooter>
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowConfirmModal(false)}
            sx={{ mr: 2 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmAction}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </AnimatedModal>

      <AddressModal
        show={showAddressModal}
        closeModal={closeAddressModal}
        deliveryCenterId={deliveryCenterId}
        cep={cep}
        street={street}
        city={city}
        uf={uf}
        houseNumber={houseNumber}
        complement={complement}
        reference={reference}
        neighborhood={neighborhood}
        setCity={setCity}
        setStreet={setStreet}
        setCep={setCep}
        handleConfirmModal={handleConfirmModal}
        setComplement={setComplement}
        setHouseNumber={setHouseNumber}
        setUf={setUf}
        setNeighborhood={setNeighborhood}
        setReference={setReference}
        onConfirm={closeAddressModal}
      />

      <ContactModal
        show={showContactModal}
        phoneDDD={phoneDDD}
        phoneNumber={phoneNumber}
        setPhoneDDD={setphoneDDD}
        setPhoneNumber={setPhoneNumber}
        closeModal={closeContactModal}
      />

      <FiscalDataModal
        show={showFiscalDataModal}
        closeModal={closeFiscalDataModal}
        cep={cep}
        street={street}
        city={city}
        uf={uf}
        houseNumber={houseNumber}
        complement={complement}
        reference={reference}
        neighborhood={neighborhood}
      />

      <PaymentMethodsModal
        show={showPaymentMethodsModal}
        closeModal={closePaymentMethodsModal}
      />

      <DiscountCouponsModal
        show={showDiscountCouponsModal}
        closeModal={closeDiscountCouponsModal}
      />

      <DeliveryRadiusListModal
        show={showDeliveryRadiusModal}
        closeModal={closeDeliveryRadiusModal}
      />

      {showDeliveryIntegrationsModal && (
        <DeliveryIntegrationsListModal
          show={showDeliveryIntegrationsModal}
          closeModal={closeDeliveryIntegrationsModal}
        />
      )}

      <OperatorsModal
        show={showOperatorsModal}
        closeModal={closeOperatosModal}
        saloonFee={saloonFee}
        counterFee={counterFee}
      />

      <DevicesListModal
        show={showPrintersModal}
        closeModal={closePrintersModal}
      />

      <DeliveryTeamModal
        show={showDeliveryGuysModal}
        closeModal={closeDeliveryGuysModal}
      />

      <DeliveryConfirmationConfigsModal
        show={showiFoodDeliveryServicesModal}
        closeModal={closeIfoodDeliveryServicesModal}
      />

      <DeliveryServicesModal
        show={showDeliveryServicesModal}
        closeModal={closeDeliveryServicesModal}
      />

      <ShippingRoutesConfigModal
        show={showShippingRoutesConfigModal}
        closeModal={closeShippingRoutesConfigModal}
      />

      <SmartRoutingConfigModal
        show={showSmartRoutingConfigModal}
        closeModal={closeSmartRoutingConfigModal}
      />

      <WorkShiftListModal
        show={showWorkShiftsModal}
        closeModal={closeWorkShiftsModal}
      />

      <EquipmentListModal
        show={showEquipmentsModal}
        closeModal={closeEquipmentsModal}
      />

      <NeighborhoodGroupListModal
        show={showNeighborhoodGroupModal}
        closeModal={closeNeighborhoodGroupModal}
      />

      <DashboardScreenConfigsModal
        closeModal={closeDashboardScreenConfig}
        show={showDashboardScreenConfigsModal}
      />

      <CashierTypeConfigsModal
        closeModal={closeCashierTypesModal}
        show={showCashierTypesModal}
      />

      <OrderTicketConfigsModal
        closeModal={closeOrderTicketModal}
        show={showOrderTicketModal}
      />

      <OrderFlowConfigsModal
        closeModal={closeOrderFlowConfigsModal}
        show={showOrderFlowConfigsModal}
      />

      <IfoodChatManagerModal
        closeModal={closeIfoodChatConfigModal}
        show={showIfoodChatConfigModal}
      />

      <AutoPilotConfigModal
        closeModal={closeAutoPilotConfigModal}
        show={showAutoPilotConfigModal}
      />

      <AgentsModal show={showAgentsModal} closeModal={closeAgentsModal} />

      <NotificationsConfigModal
        show={showNotificationsModal}
        closeModal={closeNotificationsModal}
      />

      <Food99StoresModal show={showFood99Modal} closeModal={closeFood99Modal} />
      <WhatsAppInstanceModal
        show={showWhatsAppModal}
        closeModal={closeWhatsAppModal}
      />

      <FiscalBotNumbersModal
        show={showFiscalBotNumbersModal}
        closeModal={closeFiscalBotNumbersModal}
      />
    </Box>
  );
}
